<script>
    import { preventDefault } from 'svelte/legacy';

    import { onMount, onDestroy } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    import { AssetField } from '../../../AssetField.js';
    import { ImageField } from '../../../ImageField.js';
    import { getPublicUrl } from '../../../getPublicUrl.js';
    import { getDataApiUrl } from '../../../getDataApiUrl.js';
    import { removeHost } from '../../../removeHost.js';

    // 定数定義
    const ASSET_UPLOADER_CHECK_INTERVAL = 100;
    const ASSET_UPLOADER_TIMEOUT = 3000;
    const SELECT_CHECK_INTERVAL = 50;
    const SELECT_TIMEOUT = 5000;

    let { mtappVars, id, item = $bindable() } = $props();
    let assetFieldInstance;
    let checkInterval = null;
    let timeoutId = null;

    let assetAddButtonLabel = $derived(item.buttonLabel ? item.buttonLabel : `${item.label}を選択`);

    const dispatch = createEventDispatcher();
    function triggerInputChange() {
        dispatch('inputchange');
    }

    const assetFieldOptions = {
        thumbnailWidth: item.thumbnailWidth || 120,
        thumbnailHeight: item.thumbnailHeight || 120,
        uploadDirectory: item.uploadDirectory || ''
    };

    onMount(async () => {
        // 後方互換: data がオブジェクト形式（Pro 由来の defaultFieldBlocks では
        // asset/image の data が { id, url, thumbnail, alt } と定義されている）で
        // 保存されている場合、v1 が前提とするスカラー（アセットID）へ正規化する。
        // 空オブジェクト {} は未選択とみなして '' にし、assetinfo/[object Object] の 404 を防ぐ。
        if (item.data && typeof item.data === 'object') {
            const assetData = item.data;
            if (!item.url && assetData.url) {
                item.url = assetData.url;
            }
            if (item.type === 'image') {
                if (!item.thumbnail && assetData.thumbnail) {
                    item.thumbnail = assetData.thumbnail;
                }
                if (!item.alt && assetData.alt) {
                    item.alt = assetData.alt;
                }
            }
            item.data = assetData.id || '';
        }

        // AssetUploaderはtype="module"で読み込まれるため、初期化完了を待つ必要がある
        const initializeAssetField = () => {
            // 画像の場合はImageField、それ以外はAssetFieldを使用
            if (item.type === 'image' && window.MT?.AssetUploader) {
                // MT9の場合はImageFieldを使用
                // ImageFieldは storage[storageKey] の形式を想定しているため、
                // itemオブジェクトをラップして互換性を持たせる
                const storageWrapper = {
                    asset: {
                        get id() { return item.data; },
                        set id(value) { item.data = value; },
                        get url() { return item.url; },
                        set url(value) { item.url = value; },
                        get thumbnail() { return item.thumbnail; },
                        set thumbnail(value) { item.thumbnail = value; },
                        get alt() { return item.alt; },
                        set alt(value) { item.alt = value; }
                    }
                };
                assetFieldInstance = new ImageField(id, item.type, assetFieldOptions, storageWrapper, 'asset', triggerInputChange);
            } else {
                // MT8以前またはファイルの場合はAssetFieldを使用
                // thumbnailWidth/Height を渡し、アップロード時もメディア選択時も
                // 指定寸法のサムネイルに揃える（version:3 と同じ挙動）。
                assetFieldInstance = new AssetField(id, item.type, '', assetFieldOptions);
                if (typeof item.uploadDirectory === 'string') {
                    window.mtappVars.__system.assetField.uploadDirectories[id] = item.uploadDirectory;
                } else if (Array.isArray(item.uploadDirectory)) {
                    window.mtappVars.__system.assetField.uploadDirectories[id] = [...item.uploadDirectory];
                }
            }
        };

        // MT9のAssetUploader (type="module")の読み込み完了を待機
        if (item.type === 'image') {
            if (window.MT?.AssetUploader) {
                initializeAssetField();
            } else {
                // ES Moduleの読み込み完了を待機
                checkInterval = setInterval(() => {
                    if (window.MT?.AssetUploader) {
                        clearInterval(checkInterval);
                        checkInterval = null;
                        initializeAssetField();
                    }
                }, ASSET_UPLOADER_CHECK_INTERVAL);

                // タイムアウト設定（MT8環境またはAssetUploader未インストール）
                timeoutId = setTimeout(() => {
                    if (checkInterval) {
                        clearInterval(checkInterval);
                        checkInterval = null;
                    }
                    if (!assetFieldInstance) {
                        initializeAssetField();
                    }
                }, ASSET_UPLOADER_TIMEOUT);
            }
        } else {
            // ファイルタイプの場合は即座に初期化
            initializeAssetField();
        }

        // dataがあるがthumbnailやurlが欠けている場合、DataAPIで取得する
        if (item.data && (!item.url || (item.type === 'image' && !item.thumbnail))) {
            await fetchAssetDetails();
        }
    });

    onDestroy(() => {
        // クリーンアップ: インターバルとタイムアウトをクリア
        if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
        }
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    });

    /**
     * Data APIを使用してアセット情報を取得し、欠けているデータを補完する
     */
    async function fetchAssetDetails() {
        // data はスカラー（アセットID）のみ受け付ける。
        // オブジェクトを URL に埋め込んで assetinfo/[object Object] を叩く事故を防ぐ二重防御。
        const assetId = typeof item.data === 'string' || typeof item.data === 'number' ? String(item.data) : '';
        if (!assetId) {
            return;
        }

        try {
            const apiUrl = getDataApiUrl();

            // アセット情報を取得（カスタムエンドポイント）
            const assetInfoUrl = `${apiUrl}/assetinfo/${assetId}`;
            const assetInfoResponse = await fetch(assetInfoUrl, { method: 'GET' });

            if (!assetInfoResponse.ok) {
                throw new Error(`HTTP error! status: ${assetInfoResponse.status}`);
            }

            const assetInfo = await assetInfoResponse.json();

            if (assetInfo.error) {
                console.error('Error fetching asset info:', assetInfo.error.message);
                return;
            }

            // URLが欠けている場合は設定
            if (!item.url && assetInfo.url) {
                item.url = removeHost(assetInfo.url);
                triggerInputChange();
            }

            // 画像の場合、サムネイル情報も取得
            if (item.type === 'image' && !item.thumbnail) {
                const assetSiteId = assetInfo.siteId || window.mtappVars.blog_id;
                const thumbnailWidth = item.thumbnailWidth || 120;
                const thumbnailHeight = item.thumbnailHeight || 120;

                const params = new URLSearchParams({
                    width: String(thumbnailWidth),
                    height: String(thumbnailHeight)
                });
                const thumbnailUrl = `${apiUrl}/sites/${assetSiteId}/assets/${assetId}/thumbnail?${params}`;
                const thumbnailResponse = await fetch(thumbnailUrl, { method: 'GET' });

                if (!thumbnailResponse.ok) {
                    throw new Error(`HTTP error! status: ${thumbnailResponse.status}`);
                }

                const thumbnailData = await thumbnailResponse.json();

                if (thumbnailData.error) {
                    console.error('Error fetching thumbnail:', thumbnailData.error.message);
                    return;
                }

                if (thumbnailData.url) {
                    item.thumbnail = removeHost(thumbnailData.url);
                    triggerInputChange();
                }
            }
        } catch (error) {
            console.error('Error in fetchAssetDetails:', error);
        }
    }

    /**
     * URL からファイル名を取得する
     *
     * @param {string} url
     */
    function getFilename(url) {
        return url ? new URL(url, url.startsWith('http') ? undefined : window.location.origin).pathname.split('/').pop() : '';
    }
    function select() {
        if (!assetFieldInstance) {
            console.warn('[MultiFieldAsset] assetFieldInstance is not initialized yet. Waiting for initialization...');
            // 初期化完了まで待機してからselect()を実行
            let selectCheckInterval = setInterval(() => {
                if (assetFieldInstance) {
                    clearInterval(selectCheckInterval);
                    assetFieldInstance.select();
                }
            }, SELECT_CHECK_INTERVAL);

            // タイムアウト設定
            setTimeout(() => {
                clearInterval(selectCheckInterval);
                if (!assetFieldInstance) {
                    console.error('[MultiFieldAsset] Initialization timeout. AssetField could not be initialized.');
                    alert('画像選択機能の初期化に失敗しました。ページを再読み込みしてください。');
                }
            }, SELECT_TIMEOUT);
            return;
        }
        return assetFieldInstance.select();
    }

    function remove() {
        item.data = '';
        item.url = '';
        if (item.type === 'image') {
            item.thumbnail = '';
        }
        triggerInputChange();
        if (!assetFieldInstance) {
            console.warn('[MultiFieldAsset] assetFieldInstance is not initialized yet for remove operation. Waiting for initialization...');
            // 初期化完了まで待機してからremove()を実行
            let removeCheckInterval = setInterval(() => {
                if (assetFieldInstance) {
                    clearInterval(removeCheckInterval);
                    assetFieldInstance.remove();
                }
            }, SELECT_CHECK_INTERVAL);

            // タイムアウト設定
            setTimeout(() => {
                clearInterval(removeCheckInterval);
                if (!assetFieldInstance) {
                    console.error('[MultiFieldAsset] Initialization timeout. AssetField could not be initialized for remove operation.');
                    alert('画像削除機能の初期化に失敗しました。ページを再読み込みしてください。');
                }
            }, SELECT_TIMEOUT);
            return;
        }
        return assetFieldInstance.remove();
    }
</script>

<div data-handle={item.type}>
    <input id={id} class="form-control mtapp-mf-field-{item.type}" type="hidden" bind:value={item.data} onblur={triggerInputChange} />
    <input id={id + '-url'} class="form-control" type="hidden" bind:value={item.url} onblur={triggerInputChange} />
    {#if item.type === 'image'}
        <input
            id={id + '-thumb'}
            class="form-control mtapp-mf-field-thumbnail"
            type="hidden"
            bind:value={item.thumbnail}
            onblur={triggerInputChange} />
    {/if}

    {#if item.url || item.thumbnail}
        <a href={getPublicUrl(item.url || item.thumbnail)} target="_blank" class="d-block pb-2">
            {#if item.type === 'image' && item.thumbnail}
                <img src={getPublicUrl(item.thumbnail)} class="img-fluid" alt={item.label} />
            {:else}
                <svg role="img" class="mt-icon">
                    <use xlink:href={mtappVars.static_path + '/images/sprite.svg#ic_file'} />
                </svg>
                {getFilename(item.url || item.thumbnail)}
            {/if}
        </a>
    {/if}

    <div class="btn-group" role="group">
        <button type="button" class="btn btn-default" onclick={preventDefault(select)}>{assetAddButtonLabel}</button>
        {#if item.url || item.thumbnail}
            <button type="button" onclick={preventDefault(remove)} class="btn btn-default p-1 assetField-remove-image" aria-label="Remove">
                <svg role="img" class="mt-icon">
                    <use xlink:href={mtappVars.static_path + '/images/sprite.svg#ic_trash'} />
                </svg>
            </button>
        {/if}
    </div>

    {#if typeof item.textFieldLabel !== 'undefined' && item.textFieldLabel}
        <div class="mt-3 mb-0">
            <label for={id + '-alt'}>{item.textFieldLabel}</label>
            <input id={id + '-alt'} class="form-control" type="text" bind:value={item.alt} onblur={triggerInputChange} />
        </div>
    {/if}
</div>
